// the fields a glossary column keeps, so a column added from the language menu reads back the same once saved
export const toGlossaryHeader = ({ name, language, isCustom }) => ({
  name,
  language,
  ...(isCustom && { isCustom: true }),
});

export const mapEntityEntriesToTableEntries = (entries) => {
  const headers = [];
  const rows = [];

  entries?.forEach((entry) => {
    headers.push(toGlossaryHeader(entry));
    entry.phrases?.forEach((word, index) => {
      if (rows[index]) {
        rows[index].push(word);
      } else {
        rows.push([word]);
      }
    });
  });

  return { headers, rows };
};

export const mapTableEntriesToEntityEntries = (headers, rows) => {
  const transposed = rows[0].map((_, colIndex) => rows.map((row) => row[colIndex]));
  const entries = headers.map((header, index) => ({ ...header, phrases: transposed[index] }));
  return entries;
};

const getBaseLanguage = (language) => language.split('-')[0].trim().toUpperCase();

/**
 * Whether the columns can produce at least one provider glossary. A glossary needs two language columns, and a custom
 * language replaces its parent's column (same code), so a custom column only pairs with regular columns of other
 * languages, e.g. regular DE + custom with parent DE can never build a glossary.
 */
export const canBuildGlossary = (headers) => {
  const regular = headers.filter(({ isCustom }) => !isCustom);
  const custom = headers.filter(({ isCustom }) => isCustom);

  if (regular.length >= 2) return true;

  return custom.some(
    ({ language }) => regular.filter((header) => getBaseLanguage(header.language) !== getBaseLanguage(language)).length,
  );
};

// a language is found by its name or its code, in any case, e.g. "French", "fr-ca" or "FR-CA"
export const matchesLanguageSearch = ({ name, language }, search) => {
  const term = (search ?? '').trim().toLowerCase();
  return [name, language].some((value) => (value ?? '').toLowerCase().includes(term));
};

// RFC 4180: fields with commas, quotes or line breaks must be quoted, quotes are escaped by doubling them
const escapeCSVField = (value) => {
  const text = value == null ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const toCSVLine = (cells) => cells.map(escapeCSVField).join(',');

export const downloadAsCSV = (headers, rows, title) => {
  // Step 1 Extract column names
  const headerRow = toCSVLine(headers.map((header) => header.name)); // Header row
  // Step 2: Convert rows into CSV format
  const csvRows = rows.map(toCSVLine);

  // Step 3: Combine headers and rows
  const csvContent = [headerRow, ...csvRows].join('\n');

  // Step 4: Create a Blob and download the file, the BOM lets Excel/Numbers read non-latin text (JA, KO...) as UTF-8
  const blob = new Blob(['\ufeff', csvContent], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = title;
  document.body.appendChild(a);
  a.click();

  // Cleanup
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// RFC 4180 style parser: handles quoted fields, escaped quotes, delimiters and newlines inside quotes.
// A quote only opens a quoted field at the start of a field, so a stray quote in the text (e.g. 5") stays literal.
const parseDelimited = (text, delimiter) => {
  const content = text.replace(/^\ufeff/, '');
  const records = [];
  let record = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    if (inQuotes) {
      if (char === '"' && content[i + 1] === '"') {
        field += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else if (char === '"' && field === '') {
      inQuotes = true;
    } else if (char === delimiter) {
      record.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && content[i + 1] === '\n') i++;
      record.push(field);
      records.push(record);
      record = [];
      field = '';
    } else {
      field += char;
    }
  }
  if (field !== '' || record.length) {
    record.push(field);
    records.push(record);
  }

  return records;
};

export const parseCSV = (text) => parseDelimited(text, ',').filter((r) => r.some((cell) => cell.trim() !== ''));

/**
 * Parses text copied from a spreadsheet (tab separated, cells with line breaks come wrapped in quotes).
 * Empty rows are kept so the pasted rows stay aligned, only the trailing line break is ignored.
 */
export const parseClipboardTable = (text) => {
  const records = parseDelimited(text, '\t');
  const last = records[records.length - 1];
  if (last && last.length === 1 && last[0] === '') records.pop();
  return records;
};

/**
 * Replaces the content of the existing columns with the CSV content, for the languages present in the CSV.
 * Columns are matched by header name (case-insensitive), CSV columns that don't exist in the glossary are ignored.
 * Returns the new rows, or null when no CSV column matched.
 */
export const applyCSVToRows = (csvText, headers, rows) => {
  const [csvHeaders, ...csvRows] = parseCSV(csvText);
  if (!csvHeaders) return null;

  const normalize = (value) => (value || '').trim().toLowerCase();
  const columnMap = []; // [glossaryColIndex, csvColIndex]
  headers.forEach((header, colIndex) => {
    const csvIndex = csvHeaders.findIndex((name) => normalize(name) === normalize(header.name));
    if (csvIndex !== -1) columnMap.push([colIndex, csvIndex]);
  });
  if (!columnMap.length) return null;

  const rowCount = Math.max(rows.length, csvRows.length);
  return Array.from({ length: rowCount }, (_, rowIndex) => {
    const newRow = headers.map((_, colIndex) => rows[rowIndex]?.[colIndex] ?? '');
    columnMap.forEach(([colIndex, csvIndex]) => {
      newRow[colIndex] = csvRows[rowIndex]?.[csvIndex] ?? '';
    });
    return newRow;
  });
};
